<template>
	<div class="row">
		<div class="col-xs-12">
			<div class="large">
			<FullCalendar :events="calendar"
										defaultView="dayGridMonth"
										:weekends="true"
										:selectable="true"
										:plugins="calendarPlugins"
										:header="headerToolbar"
										:views="views"
										:buttonText="todayButtonText"
										ref="calendar"
										:datesRender="loadCalendar"
										:eventRender="updateEvents"
										:locale="getSelectedLocale.LOCALE_CODE !== 'sa-us' ? getSelectedLocale.LOCALE_CODE : 'en-us'"
										@eventClick="eventClicked" />
			</div>

			<div class="small">
			<FullCalendar :events="calendar"
										defaultView="dayGridMonth"
										:weekends="true"
										:selectable="true"
										:plugins="calendarPlugins"
										:header="smallHeaderToolbar"
										:footer="smallFooterToolbar"
										:views="views"
										:buttonText="todayButtonText"
										ref="calendar"
										:datesRender="loadCalendar"
										:eventRender="updateEvents"
										:locale="getSelectedLocale.LOCALE_CODE !== 'sa-us' ? getSelectedLocale.LOCALE_CODE : 'en-us'"
										@eventClick="eventClicked" />
			</div>
		</div>

		<!-- TODO: Add modal popup with detailed info -->
		<modal :modal-title="modalData.COURSE"
					 :show="showModal"
					 dialog-size="lg"
					 name="incidentDescription"
					 fields=""
					 @close="closeModal()">
			<div v-html="modalData.DESCRIPTION"></div>
			<br />
			<span>
				<strong>{{ $t('MAIN_CONTENT.StudentCalendar.startDateTitleCaseWithColon') }} </strong> {{ modalData.START_DATE }}
			</span>
			<br />
			<span>
				<strong>{{ $t('MAIN_CONTENT.StudentCalendar.dueDateTitleCaseWithColon') }} </strong> {{ modalData.END_DATE }}
			</span>
			<template slot="footer" :close="closeModal">
				<button type="button" class="btn btn-default" @click="closeModal">
					{{ $t('MAIN_CONTENT.commonContent.closeButton') }}
				</button>
			</template>
		</modal>
	</div>
</template>

<script>
	import moment from 'moment';
	import { mapGetters } from 'vuex';

	import Calendar from '../../api/Calendar.js';

	import Modal from 'COMMONS/shared/Modal/Modal.vue';

	/* FullCalendar has a locale attribute that can be applied to convert everything to the required format so locale text
	** is unnecessary. https://fullcalendar.io/docs/locale
	*/
	import FullCalendar from '@fullcalendar/vue';
	import dayGridPlugin from '@fullcalendar/daygrid';
	import timeGridPlugin from '@fullcalendar/timegrid';
	import interactionPlugin from '@fullcalendar/interaction';
	import list from '@fullcalendar/list';

	import es from "@fullcalendar/core/locales/es";

	import "@fullcalendar/core/main.css";
	import "@fullcalendar/daygrid/main.css";
	import "@fullcalendar/timegrid/main.css";

	export default {
		data() {
			return {
				isLoading:				false,
				calendar:				[],
				calendarPlugins: [	// plugins must be defined in the JS
					dayGridPlugin,
					timeGridPlugin,
					interactionPlugin, // needed for dateClick
					list
				],
				headerToolbar: {
					left: 'dayGridMonth,timeGridWeek,timeGridDay,list',
					center: 'title',
					right: 'prevYear,prev,next,nextYear,today'
				},
				smallHeaderToolbar: {
					left: '',
					center: 'title',
					right: 'dayGridMonth,timeGridWeek,timeGridDay,list'
				},
				smallFooterToolbar: {
					left: 'prevYear,prev,next,nextYear,today',
					center: '',
					right: ''
				},
				modalData: {},
				showModal: false
			};
		},
		computed: {
			...mapGetters( [ 'selectedStudent', 'getSelectedLocale' ] ),
			views() {
				return {
					dayGridMonth: { buttonText: this.$t( 'MAIN_CONTENT.StudentCalendar.month' ) },
					timeGridWeek: { buttonText: this.$t( 'MAIN_CONTENT.StudentCalendar.week' ) },
					timeGridDay: { buttonText: this.$t( 'MAIN_CONTENT.StudentCalendar.day' ) },
					list: { buttonText: this.$t( 'MAIN_CONTENT.StudentCalendar.list' ) },
					today: { buttonText: this.$t( 'MAIN_CONTENT.StudentCalendar.today' ) }
				};
			},
			todayButtonText() {
				return { today: this.$t( 'MAIN_CONTENT.StudentCalendar.today' ) };
			},
		},
		watch: {
			'$store.state.profile.LOCALE.LOCALE_CODE'( newValue, oldValue ) {
				if( oldValue !== newValue ) {
					this.$set(
						this,
						'views',
						{
							dayGridMonth: { buttonText: this.$t( 'MAIN_CONTENT.StudentCalendar.month' ) },
							timeGridWeek: { buttonText: this.$t( 'MAIN_CONTENT.StudentCalendar.week' ) },
							timeGridDay: { buttonText: this.$t( 'MAIN_CONTENT.StudentCalendar.day' ) },
							list: { buttonText: this.$t( 'MAIN_CONTENT.StudentCalendar.list' ) },
							today: { buttonText: this.$t( 'MAIN_CONTENT.StudentCalendar.today' ) }
						}
					);

					this.todayButtonText = { today: this.$t( 'MAIN_CONTENT.StudentCalendar.today' ) };
				}
			}
		},
		components: {
			FullCalendar,
			Modal
		},
		methods: {
			closeModal() {
				this.showModal = false;
			},
			eventClicked(data) {
				this.modalData = data.event.extendedProps;
				this.showModal = true;
			},
			updateEvents(info) {
				info.el.tabIndex = 0;
				info.el.setAttribute('aria-label', `${info.event.start} ${info.event.title}`);
			},
			loadCalendar() {
				const self = this;
				/* call once and dont call until last of is done */
				if(this.isLoading){
					return;
				}
				this.isLoading = true;
				let calendar = new Calendar({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID
					}
				});

				const promise = calendar.get();

				promise.then(response => {
					let events = calendar.getResponseData();

					for(let c in events) {
						let event = events[c];

						event.title = event.TITLE;
						event.start = moment(event.START).format('YYYY-MM-DD');
						event.end = moment(event.END_DATE).format('YYYY-MM-DD');
					}

					self.$set(self, 'calendar', events);
					this.isLoading = false;
				}).catch((error)=>{
					/* if error reset */
					this.isLoading = false;
				});
			}
		},
		created() {
			this.$store.commit('setTitleIcon', 'school');
			this.$store.commit('setTitle', 'MAIN_CONTENT.StudentCalendar.sideMenuStudentCalendarLink');
			this.$store.commit('setUseHeader', true);

			this.loadCalendar();
		}
	};
</script>
